import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Star, 
  MapPin, 
  Globe, 
  Heart,
  MessageCircle,
  Award,
  X,
  ArrowLeft,
  BookOpen,
  Clock,
  CheckCircle,
  DollarSign,
  Calendar,
  Briefcase,
  Users,
  ChevronDown,
  Filter,
  RotateCcw
} from 'lucide-react';
import { api } from '../services/api';
import LazyImage from '../components/ui/LazyImage';

const TutorsGridPage = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [savedTutors, setSavedTutors] = useState(new Set());
  const [filterAvailable, setFilterAvailable] = useState(false);
  
  // New filter states
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [languageFilter, setLanguageFilter] = useState({ mandarin: true, cantonese: true }); // Both checked by default
  const [curriculumFilter, setCurriculumFilter] = useState('all'); // 'all' or specific curriculum
  const [showFilters, setShowFilters] = useState(false); // false or 'price' or 'language' or 'curriculum'
  const [priceDistribution, setPriceDistribution] = useState([]);

  // Inspirational quotes - rotate on each page load
  const inspirationalQuotes = [
    { chinese: '语言是桥梁', english: 'LANGUAGE IS A BRIDGE' },
    { chinese: '学无止境', english: 'LEARNING HAS NO LIMITS' },
    { chinese: '每个字都是一扇窗', english: 'EVERY CHARACTER IS A WINDOW' },
    { chinese: '一字一世界', english: 'ONE CHARACTER, ONE WORLD' },
    { chinese: '文字传承文化', english: 'CHARACTERS CARRY CULTURE' }
  ];
  
  const [randomQuote] = useState(() => 
    inspirationalQuotes[Math.floor(Math.random() * inspirationalQuotes.length)]
  );

  // Calculate price distribution for histogram
  const calculatePriceDistribution = (tutorList) => {
    if (tutorList.length === 0) return [];

    const rates = tutorList.map(t => t.hourlyRate);
    const minRate = Math.min(...rates);
    const maxRate = Math.max(...rates);
    const range = maxRate - minRate;
    
    // Create 20 bins
    const numBins = 20;
    const binSize = range / numBins;
    const bins = Array(numBins).fill(0);

    // Count tutors in each bin
    rates.forEach(rate => {
      const binIndex = Math.min(Math.floor((rate - minRate) / binSize), numBins - 1);
      bins[binIndex]++;
    });

    // Find max count for normalization
    const maxCount = Math.max(...bins);

    // Create distribution array with normalized heights
    return bins.map((count, index) => ({
      count,
      height: maxCount > 0 ? (count / maxCount) * 100 : 0,
      minPrice: minRate + (index * binSize),
      maxPrice: minRate + ((index + 1) * binSize)
    }));
  };

  // Curriculum options based on language
  const MANDARIN_CURRICULUMS = [
    { value: 'hsk', label: 'HSK' },
    { value: 'cambridge_igcse', label: 'Cambridge IGCSE' },
    { value: 'edexcel', label: 'Edexcel' },
    { value: 'aqa_gcse', label: 'AQA GCSE' },
    { value: 'alevel', label: 'A-Level' },
    { value: 'ap_chinese', label: 'AP Chinese' }
  ];

  const CANTONESE_CURRICULUMS = [
    { value: 'hkdse', label: 'HKDSE' }
  ];

  // Load tutors on mount
  useEffect(() => {
    loadTutors();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = 'Tutors';
  }, []);

  const loadTutors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all tutors
      const allTutors = await api.tutors.getApplications('all');
      
      // Filter out rejected tutors (only show pending and approved)
      const tutorsData = allTutors.filter(tutor => tutor.status !== 'rejected');
      
      // Transform tutor data to match our display format
      const formattedTutors = tutorsData.map(tutor => ({
        id: tutor.$id,
        name: `${tutor.firstName} ${tutor.lastName}`.toUpperCase(),
        code: `${tutor.firstName[0]}${tutor.lastName[0]}-${tutor.$id.slice(-3)}`.toUpperCase(),
        title: tutor.curriculums?.[0]?.toUpperCase() || 'CHINESE TUTOR',
        subtitle: tutor.curriculums?.[1]?.toUpperCase() || '',
        photo: tutor.profilePhotoId 
          ? `${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.profilePhotoId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`
          : null,
        rating: 4.8, // Default rating - you may want to add ratings to your database
        reviewCount: Math.floor(Math.random() * 150) + 20, // Placeholder - add real reviews later
        hourlyRate: tutor.hourlyRateOnline || 400,
        languages: extractLanguages(tutor),
        experience: parseInt(tutor.yearsExperience) || 0,
        specialties: tutor.curriculums || [],
        location: tutor.baseDistricts?.[0]?.toUpperCase() || 'HONG KONG',
        available: tutor.locationPreference !== 'unavailable', // Adjust based on your availability logic
        topRated: parseInt(tutor.yearsExperience) >= 8, // Top rated if 8+ years experience
        willTravel: tutor.locationPreference === 'in_person_only' || tutor.locationPreference === 'both', // Shows if tutor offers in-person (travels to student)
        bio: tutor.tutorBio || 'Experienced Chinese language tutor.',
        expertise: tutor.teachingStyles || [],
        teachingStyle: tutor.teachingStyles?.[0]?.toUpperCase() || 'PERSONALIZED',
        availability: formatAvailability(tutor.availability),
        studentQuote: 'Great tutor!', // Placeholder - add real testimonials later
        studentName: 'STUDENT',
        // Store original data for booking
        originalData: tutor
      }));
      
      setTutors(formattedTutors);
      
      // Set initial price range based on actual tutor rates
      if (formattedTutors.length > 0) {
        const rates = formattedTutors.map(t => t.hourlyRate);
        const minRate = Math.min(...rates);
        const maxRate = Math.max(...rates);
        setPriceRange([minRate, maxRate]);
        
        // Calculate price distribution
        setPriceDistribution(calculatePriceDistribution(formattedTutors));
      }
    } catch (err) {
      console.error('Error loading tutors:', err);
      setError('Failed to load tutors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract languages from tutor curriculums
  const extractLanguages = (tutor) => {
    const languages = new Set();
    
    // Check curriculums for language indicators
    if (tutor.curriculums) {
      tutor.curriculums.forEach(curr => {
        if (curr.includes('mandarin') || curr.includes('hsk') || curr.includes('ap_chinese')) {
          languages.add('Mandarin');
        }
        if (curr.includes('cantonese') || curr.includes('hkdse')) {
          languages.add('Cantonese');
        }
      });
    }
    
    // Default to Mandarin if no specific language found
    if (languages.size === 0) {
      languages.add('Mandarin');
    }
    
    // Always add English as most tutors speak it
    languages.add('English');
    
    return Array.from(languages);
  };

  // Helper function to format availability
  const formatAvailability = (availability) => {
    if (!availability) return 'FLEXIBLE';
    
    // If availability is a JSON string, parse it
    let avail = availability;
    if (typeof availability === 'string') {
      try {
        avail = JSON.parse(availability);
      } catch (e) {
        return 'FLEXIBLE';
      }
    }
    
    // Count available slots
    const availableSlots = Object.values(avail).filter(v => v === true).length;
    
    if (availableSlots === 0) return 'LIMITED';
    if (availableSlots > 20) return 'HIGHLY FLEXIBLE';
    return 'FLEXIBLE';
  };

  const toggleSaved = (tutorId) => {
    const newSaved = new Set(savedTutors);
    if (newSaved.has(tutorId)) {
      newSaved.delete(tutorId);
    } else {
      newSaved.add(tutorId);
    }
    setSavedTutors(newSaved);
  };

  // Enhanced filtering logic
  const filteredTutors = tutors.filter(tutor => {
    // Available filter
    if (filterAvailable && !tutor.available) return false;
    
    // Price range filter
    if (tutor.hourlyRate < priceRange[0] || tutor.hourlyRate > priceRange[1]) return false;
    
    // Language filter - only filter if at least one is unchecked
    if (!languageFilter.mandarin || !languageFilter.cantonese) {
      const hasMandarin = tutor.languages.some(lang => lang.toLowerCase() === 'mandarin');
      const hasCantonese = tutor.languages.some(lang => lang.toLowerCase() === 'cantonese');
      
      // If neither language is selected, show nothing
      if (!languageFilter.mandarin && !languageFilter.cantonese) return false;
      
      // If only Mandarin is selected, must have Mandarin
      if (languageFilter.mandarin && !languageFilter.cantonese && !hasMandarin) return false;
      
      // If only Cantonese is selected, must have Cantonese
      if (!languageFilter.mandarin && languageFilter.cantonese && !hasCantonese) return false;
    }
    
    // Curriculum filter
    if (curriculumFilter !== 'all') {
      const hasCurriculum = tutor.specialties.some(specialty => {
        const spec = specialty.toLowerCase();
        const filter = curriculumFilter.toLowerCase();
        
        // Match curriculum patterns
        if (filter === 'hsk' && spec.includes('hsk')) return true;
        if (filter === 'cambridge_igcse' && (spec.includes('cambridge') && spec.includes('igcse'))) return true;
        if (filter === 'edexcel' && spec.includes('edexcel')) return true;
        if (filter === 'aqa_gcse' && spec.includes('aqa')) return true;
        if (filter === 'alevel' && spec.includes('alevel')) return true;
        if (filter === 'ap_chinese' && spec.includes('ap_chinese')) return true;
        if (filter === 'hkdse' && spec.includes('hkdse')) return true;
        
        return false;
      });
      if (!hasCurriculum) return false;
    }
    
    return true;
  });

  // Get curriculum options based on selected language
  const getCurriculumOptions = () => {
    const bothSelected = languageFilter.mandarin && languageFilter.cantonese;
    const onlyMandarin = languageFilter.mandarin && !languageFilter.cantonese;
    const onlyCantonese = !languageFilter.mandarin && languageFilter.cantonese;
    
    if (onlyMandarin) return MANDARIN_CURRICULUMS;
    if (onlyCantonese) return CANTONESE_CURRICULUMS;
    // Both or neither selected - show all
    return [...MANDARIN_CURRICULUMS, ...CANTONESE_CURRICULUMS];
  };

  // Reset all filters
  const resetFilters = () => {
    setFilterAvailable(false);
    setLanguageFilter({ mandarin: true, cantonese: true });
    setCurriculumFilter('all');
    const rates = tutors.map(t => t.hourlyRate);
    if (rates.length > 0) {
      setPriceRange([Math.min(...rates), Math.max(...rates)]);
      setPriceDistribution(calculatePriceDistribution(tutors));
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    const rates = tutors.map(t => t.hourlyRate);
    const minRate = rates.length > 0 ? Math.min(...rates) : 0;
    const maxRate = rates.length > 0 ? Math.max(...rates) : 1000;
    
    return filterAvailable || 
           (!languageFilter.mandarin || !languageFilter.cantonese) || // At least one language unchecked
           curriculumFilter !== 'all' ||
           priceRange[0] !== minRate ||
           priceRange[1] !== maxRate;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-800 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 uppercase tracking-widest text-sm">LOADING TUTORS...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <X size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 uppercase tracking-widest">ERROR</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={loadTutors}
            className="px-6 py-3 bg-white text-black uppercase text-xs tracking-widest font-semibold hover:bg-gray-200 transition-colors"
          >
            TRY AGAIN
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (tutors.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Users size={48} className="text-gray-700 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 uppercase tracking-widest">NO TUTORS AVAILABLE</h2>
          <p className="text-gray-400 mb-6">Check back soon for available tutors.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Minimalist Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black border-b border-gray-900">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.history.back()}
              className="text-gray-500 hover:text-white transition-colors uppercase text-xs tracking-widest"
            >
              ← BACK
            </button>
            
            <div className="text-center flex-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-widest">TUTORS</h1>
              <p className="text-xs text-gray-600 mt-1 tracking-wider">{filteredTutors.length} EDUCATORS</p>
            </div>

            <div className="w-16"></div> {/* Spacer for alignment */}
          </div>
        </div>
      </header>

      {/* Sticky Filter Bar */}
      <div className="fixed top-[88px] left-0 right-0 z-30 bg-black border-b border-gray-900">
        <div className="container mx-auto px-4 md:px-8 py-6">
          {/* Filter Type Buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={() => setShowFilters(showFilters === 'price' ? false : 'price')}
              className={`px-4 py-2 border transition-colors uppercase text-xs tracking-widest ${
                showFilters === 'price'
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              <span className="flex items-center gap-2">
                <DollarSign size={14} />
                PRICE
                {priceRange[0] !== (tutors.length > 0 ? Math.min(...tutors.map(t => t.hourlyRate)) : 0) ||
                 priceRange[1] !== (tutors.length > 0 ? Math.max(...tutors.map(t => t.hourlyRate)) : 1000) ? (
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                ) : null}
              </span>
            </button>

            <button
              onClick={() => setShowFilters(showFilters === 'language' ? false : 'language')}
              className={`px-4 py-2 border transition-colors uppercase text-xs tracking-widest ${
                showFilters === 'language'
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              <span className="flex items-center gap-2">
                <Globe size={14} />
                LANGUAGE
                {(!languageFilter.mandarin || !languageFilter.cantonese) ? (
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                ) : null}
              </span>
            </button>

            <button
              onClick={() => setShowFilters(showFilters === 'curriculum' ? false : 'curriculum')}
              className={`px-4 py-2 border transition-colors uppercase text-xs tracking-widest ${
                showFilters === 'curriculum'
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-gray-400 border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              <span className="flex items-center gap-2">
                <BookOpen size={14} />
                CURRICULUM
                {curriculumFilter !== 'all' ? (
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                ) : null}
              </span>
            </button>

            {hasActiveFilters() && (
              <button
                onClick={resetFilters}
                className="ml-auto px-4 py-2 border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-400 transition-colors uppercase text-xs tracking-widest"
              >
                <span className="flex items-center gap-2">
                  <RotateCcw size={14} />
                  RESET
                </span>
              </button>
            )}
          </div>

          {/* Filter Content Panels */}
          {showFilters === 'price' && (
            <div className="pb-4 animate-slideDown">
              <div className="bg-gray-900 border border-gray-800 rounded p-6 max-w-md">
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-4">
                  PRICE RANGE
                </label>
                <div className="space-y-3">
                  {/* Price inputs */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-gray-600 mb-1">MIN</div>
                      <input
                        type="number"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <span className="text-gray-600 mt-5">—</span>
                    <div className="flex-1">
                      <div className="text-xs text-gray-600 mb-1">MAX</div>
                      <input
                        type="number"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 1000])}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  {/* Histogram Chart with Price Labels */}
                  <div className="space-y-1">
                    <div className="relative h-16 bg-gray-800 border border-gray-700 rounded overflow-hidden">
                      {/* Bars */}
                      <div className="absolute bottom-0 left-0 right-0 h-full flex items-end gap-px px-1">
                        {priceDistribution.map((bin, index) => {
                          const isInRange = bin.maxPrice >= priceRange[0] && bin.minPrice <= priceRange[1];
                          return (
                            <div
                              key={index}
                              className={`flex-1 transition-all duration-200 ${
                                isInRange ? 'bg-purple-500' : 'bg-gray-700'
                              }`}
                              style={{ height: `${bin.height}%` }}
                              title={`${Math.round(bin.minPrice)}-${Math.round(bin.maxPrice)} HKD: ${bin.count} tutors`}
                            />
                          );
                        })}
                      </div>

                      {/* Range overlay */}
                      {tutors.length > 0 && (
                        <div className="absolute top-0 bottom-0 pointer-events-none">
                          {(() => {
                            const rates = tutors.map(t => t.hourlyRate);
                            const minRate = Math.min(...rates);
                            const maxRate = Math.max(...rates);
                            const totalRange = maxRate - minRate;
                            
                            const leftPercent = ((priceRange[0] - minRate) / totalRange) * 100;
                            const rightPercent = ((maxRate - priceRange[1]) / totalRange) * 100;
                            
                            return (
                              <>
                                <div 
                                  className="absolute top-0 bottom-0 left-0 bg-black opacity-40"
                                  style={{ width: `${Math.max(0, leftPercent)}%` }}
                                />
                                <div 
                                  className="absolute top-0 bottom-0 right-0 bg-black opacity-40"
                                  style={{ width: `${Math.max(0, rightPercent)}%` }}
                                />
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Price axis labels */}
                    {tutors.length > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>${Math.min(...tutors.map(t => t.hourlyRate))}</span>
                        <span>${Math.round((Math.min(...tutors.map(t => t.hourlyRate)) + Math.max(...tutors.map(t => t.hourlyRate))) / 2)}</span>
                        <span>${Math.max(...tutors.map(t => t.hourlyRate))}</span>
                      </div>
                    )}
                  </div>

                  {/* Range slider */}
                  <div className="relative h-8">
                    <div className="absolute top-3 w-full h-2 bg-gray-700 rounded-full" />
                    
                    {tutors.length > 0 && (() => {
                      const minRate = Math.min(...tutors.map(t => t.hourlyRate));
                      const maxRate = Math.max(...tutors.map(t => t.hourlyRate));
                      const totalRange = maxRate - minRate;
                      
                      const leftPercent = ((priceRange[0] - minRate) / totalRange) * 100;
                      const rightPercent = ((priceRange[1] - minRate) / totalRange) * 100;
                      
                      return (
                        <div 
                          className="absolute top-3 h-2 bg-purple-500 rounded-full"
                          style={{
                            left: `${leftPercent}%`,
                            right: `${100 - rightPercent}%`
                          }}
                        />
                      );
                    })()}
                    
                    <input
                      type="range"
                      min={tutors.length > 0 ? Math.min(...tutors.map(t => t.hourlyRate)) : 0}
                      max={tutors.length > 0 ? Math.max(...tutors.map(t => t.hourlyRate)) : 1000}
                      value={priceRange[0]}
                      onChange={(e) => {
                        const newMin = parseInt(e.target.value);
                        if (newMin <= priceRange[1]) {
                          setPriceRange([newMin, priceRange[1]]);
                        }
                      }}
                      className="absolute w-full range-slider"
                      style={{ zIndex: 3 }}
                    />
                    <input
                      type="range"
                      min={tutors.length > 0 ? Math.min(...tutors.map(t => t.hourlyRate)) : 0}
                      max={tutors.length > 0 ? Math.max(...tutors.map(t => t.hourlyRate)) : 1000}
                      value={priceRange[1]}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value);
                        if (newMax >= priceRange[0]) {
                          setPriceRange([priceRange[0], newMax]);
                        }
                      }}
                      className="absolute w-full range-slider"
                      style={{ zIndex: 4 }}
                    />
                  </div>

                  <div className="text-xs text-gray-600 text-center">
                    {filteredTutors.length} tutors in range
                  </div>
                </div>
              </div>
            </div>
          )}

          {showFilters === 'language' && (
            <div className="pb-4 animate-slideDown">
              <div className="bg-gray-900 border border-gray-800 rounded p-6 max-w-md">
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-4">
                  LANGUAGE
                </label>
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={languageFilter.mandarin}
                      onChange={(e) => setLanguageFilter({
                        ...languageFilter,
                        mandarin: e.target.checked
                      })}
                      className="w-5 h-5 bg-gray-800 border border-gray-700 rounded checked:bg-purple-500 checked:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-white group-hover:text-purple-400 transition-colors uppercase tracking-wider">
                      MANDARIN
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={languageFilter.cantonese}
                      onChange={(e) => setLanguageFilter({
                        ...languageFilter,
                        cantonese: e.target.checked
                      })}
                      className="w-5 h-5 bg-gray-800 border border-gray-700 rounded checked:bg-purple-500 checked:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-white group-hover:text-purple-400 transition-colors uppercase tracking-wider">
                      CANTONESE
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {showFilters === 'curriculum' && (
            <div className="pb-4 animate-slideDown">
              <div className="bg-gray-900 border border-gray-800 rounded p-6 max-w-md">
                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-4">
                  CURRICULUM
                </label>
                <select
                  value={curriculumFilter}
                  onChange={(e) => setCurriculumFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white text-sm uppercase tracking-wider focus:outline-none focus:border-purple-500"
                  disabled={!languageFilter.mandarin && !languageFilter.cantonese}
                >
                  <option value="all">ALL CURRICULUMS</option>
                  {getCurriculumOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <main className="container mx-auto px-4 md:px-8 pb-20" style={{ paddingTop: showFilters ? '260px' : '180px' }}>
        {filteredTutors.length === 0 ? (
          <div className="text-center py-20">
            <Filter size={48} className="text-gray-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2 uppercase tracking-widest">NO TUTORS MATCH YOUR FILTERS</h2>
            <p className="text-gray-400 mb-6">Try adjusting your search criteria</p>
            <button
              onClick={resetFilters}
              className="px-6 py-3 bg-white text-black uppercase text-xs tracking-widest font-semibold hover:bg-gray-200 transition-colors"
            >
              RESET FILTERS
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {filteredTutors.map((tutor) => (
              <button
                key={tutor.id}
                onClick={() => navigate(`/tutors/${tutor.id}`)}
                className="group relative bg-black overflow-hidden hover:bg-gray-950 transition-all p-6 text-left border-r border-b border-gray-900"
              >
                <div className="flex flex-col h-full">
                  {/* Circular Photo and Name Section */}
                  <div className="flex items-start gap-4 mb-4">
                    {/* Circular Photo - Lazy Loaded */}
                    <div className="w-20 h-20 flex-shrink-0">
                      {tutor.photo ? (
                        <LazyImage
                          src={tutor.photo}
                          alt={tutor.name}
                          className="relative w-20 h-20 rounded-full overflow-hidden"
                          fallback={
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-gray-900 to-gray-800 flex items-center justify-center">
                              <div className="text-2xl font-bold text-gray-700">
                                {tutor.name.split(' ')[0][0]}{tutor.name.split(' ')[1][0]}
                              </div>
                            </div>
                          }
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-900 via-gray-900 to-gray-800 flex items-center justify-center">
                          <div className="text-2xl font-bold text-gray-700 group-hover:text-purple-500 transition-colors">
                            {tutor.name.split(' ')[0][0]}{tutor.name.split(' ')[1][0]}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Name and Code */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-bold tracking-tight leading-tight">
                          {tutor.name}
                        </h3>
                        <span className="text-xs text-gray-600 tracking-widest font-mono flex-shrink-0">
                          {tutor.code}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest">
                        {tutor.title}
                      </p>
                    </div>
                  </div>

                  {/* Bio Snippet */}
                  <div className="mb-4 flex-1">
                    <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">
                      {tutor.bio}
                    </p>
                  </div>

                  {/* Bottom Section */}
                  <div className="space-y-3">
                    {/* Status Tags */}
                    <div className="flex gap-2 text-xs">
                      {tutor.topRated && (
                        <span className="px-2 py-1 bg-yellow-500 bg-opacity-10 text-yellow-500 uppercase tracking-wider border border-yellow-500 border-opacity-30">
                          TOP
                        </span>
                      )}
                      {tutor.available && (
                        <span className="px-2 py-1 bg-green-500 bg-opacity-10 text-green-500 uppercase tracking-wider border border-green-500 border-opacity-30">
                          OPEN
                        </span>
                      )}
                    </div>
                    
                    {/* Price & Rating */}
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Star size={12} className="text-yellow-500 fill-yellow-500" />
                        <span className="text-gray-400">{tutor.rating}</span>
                        <span className="text-gray-700">•</span>
                        <span className="text-gray-600 uppercase tracking-wider">
                          {tutor.experience}Y EXP
                        </span>
                      </div>
                      <div className="text-white font-bold">
                        ${tutor.hourlyRate}/HR
                      </div>
                    </div>

                    {/* Location */}
                    <div className="text-xs text-gray-600">
                      <MapPin size={12} className="inline mr-1" />
                      {tutor.location}
                      {tutor.willTravel && (
                        <>
                          <span className="text-gray-700 mx-1">•</span>
                          <span className="text-green-400">TRAVELS</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 border border-white opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none"></div>
              </button>
            ))}

            {/* Inspirational Quote Card - Always at the end */}
            <div className="bg-black p-6 flex flex-col items-center justify-center text-center border-r border-b border-gray-900">
              <div className="space-y-4">
                <div className="text-3xl md:text-4xl font-light text-purple-400 opacity-40">
                  {randomQuote.chinese}
                </div>
                <div className="text-xs text-gray-600 uppercase tracking-widest">
                  {randomQuote.english}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Expanded Tutor Modal */}
      {selectedTutor && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setSelectedTutor(null)}
        >
          <div 
            className="w-full max-w-6xl max-h-[90vh] bg-black border border-gray-900 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex h-[90vh]">
              {/* Left - Fixed Photo (40% width) - No lazy load needed, modal is user-triggered */}
              <div className="w-2/5 bg-gray-900 flex-shrink-0">
                {selectedTutor.photo ? (
                  <div className="relative w-full h-full">
                    <img 
                      src={selectedTutor.photo} 
                      alt={selectedTutor.name}
                      className="w-full h-full object-cover object-center"
                      style={{ objectPosition: 'center 30%' }}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-900 via-gray-900 to-gray-800 flex items-center justify-center">
                    <div className="text-9xl font-bold text-gray-700">
                      {selectedTutor.name.split(' ')[0][0]}{selectedTutor.name.split(' ')[1][0]}
                    </div>
                  </div>
                )}
              </div>

              {/* Right - All Content (60% width, scrollable) */}
              <div className="w-3/5 flex flex-col bg-black">
                {/* Header Section */}
                <div className="border-b border-gray-900 p-6 md:p-8 flex-shrink-0">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 tracking-widest font-mono">
                        {selectedTutor.code}
                      </span>
                      <div className="flex gap-2">
                        {selectedTutor.topRated && (
                          <div className="px-2 py-1 bg-yellow-500 bg-opacity-10 border border-yellow-500 border-opacity-30 text-xs text-yellow-500 uppercase tracking-wider">
                            TOP RATED
                          </div>
                        )}
                        {selectedTutor.available && (
                          <div className="px-2 py-1 bg-green-500 bg-opacity-10 border border-green-500 border-opacity-30 text-xs text-green-500 uppercase tracking-wider">
                            AVAILABLE
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedTutor(null)}
                      className="p-2 hover:bg-gray-900 rounded transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                    {selectedTutor.name}
                  </h2>
                  <p className="text-sm text-gray-500 uppercase tracking-widest mb-6">
                    {selectedTutor.title} {selectedTutor.subtitle && `— ${selectedTutor.subtitle}`}
                  </p>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Star size={14} className="text-yellow-500 fill-yellow-500" />
                        <span className="text-lg font-bold">{selectedTutor.rating}</span>
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        ({selectedTutor.reviewCount})
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold mb-1">
                        {selectedTutor.experience}Y
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        EXP
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold mb-1">
                        ${selectedTutor.hourlyRate}
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        /HOUR
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold mb-1">
                        {selectedTutor.languages.length}
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wider">
                        LANG
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-6 md:p-8 space-y-8">
                    {/* Bio Section */}
                    <div>
                      <h3 className="text-xs text-gray-600 uppercase tracking-widest mb-4">
                        ABOUT
                      </h3>
                      <p className="text-gray-300 leading-relaxed text-lg">
                        {selectedTutor.bio}
                      </p>
                    </div>

                    {/* Expertise */}
                    <div>
                      <h3 className="text-xs text-gray-600 uppercase tracking-widest mb-4">
                        EXPERTISE
                      </h3>
                      <div className="space-y-3">
                        {selectedTutor.expertise.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="w-1 h-1 rounded-full bg-white mt-2 flex-shrink-0"></div>
                            <span className="text-gray-400">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Specialties */}
                    <div>
                      <h3 className="text-xs text-gray-600 uppercase tracking-widest mb-4">
                        SPECIALTIES
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedTutor.specialties.map((specialty, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-white bg-opacity-5 border border-white border-opacity-10 text-xs uppercase tracking-wider"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-4">
                      <div className="flex items-start justify-between py-3 border-b border-gray-900">
                        <span className="text-xs text-gray-600 uppercase tracking-wider">
                          Location
                        </span>
                        <span className="text-sm text-right">
                          {selectedTutor.location}
                          {selectedTutor.willTravel && (
                            <span className="block text-xs text-green-400 mt-1">
                              • TRAVELS TO STUDENTS
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-start justify-between py-3 border-b border-gray-900">
                        <span className="text-xs text-gray-600 uppercase tracking-wider">
                          Languages
                        </span>
                        <span className="text-sm text-right">
                          {selectedTutor.languages.join(', ')}
                        </span>
                      </div>
                      <div className="flex items-start justify-between py-3 border-b border-gray-900">
                        <span className="text-xs text-gray-600 uppercase tracking-wider">
                          Teaching Style
                        </span>
                        <span className="text-sm text-right">
                          {selectedTutor.teachingStyle}
                        </span>
                      </div>
                      <div className="flex items-start justify-between py-3 border-b border-gray-900">
                        <span className="text-xs text-gray-600 uppercase tracking-wider">
                          Availability
                        </span>
                        <span className="text-sm text-right">
                          {selectedTutor.availability}
                        </span>
                      </div>
                    </div>

                    {/* Testimonial */}
                    {selectedTutor.studentQuote && (
                      <div className="border-l-2 border-white border-opacity-20 pl-6">
                        <p className="text-gray-400 italic mb-3">
                          "{selectedTutor.studentQuote}"
                        </p>
                        <p className="text-xs text-gray-600 uppercase tracking-wider">
                          — {selectedTutor.studentName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer - Actions (Fixed at bottom) */}
                <div className="border-t border-gray-900 p-6 md:p-8 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => toggleSaved(selectedTutor.id)}
                      className={`flex-1 py-4 border transition-all uppercase text-xs tracking-widest font-semibold ${
                        savedTutors.has(selectedTutor.id)
                          ? 'bg-white text-black border-white'
                          : 'bg-transparent text-white border-white hover:bg-white hover:text-black'
                      }`}
                    >
                      {savedTutors.has(selectedTutor.id) ? '✓ SAVED' : 'SAVE'}
                    </button>
                    <button
                      onClick={() => alert('Opening booking for ' + selectedTutor.name)}
                      className="flex-1 py-4 bg-white text-black border border-white hover:bg-transparent hover:text-white transition-all uppercase text-xs tracking-widest font-semibold"
                    >
                      BOOK TRIAL SESSION
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Counter (Fixed Bottom Right) */}
      {savedTutors.size > 0 && (
        <div className="fixed bottom-8 right-8 z-30">
          <button className="w-16 h-16 bg-white text-black rounded-full flex flex-col items-center justify-center hover:scale-110 transition-transform shadow-2xl">
            <Heart size={20} className="mb-1" />
            <span className="text-xs font-bold">{savedTutors.size}</span>
          </button>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }

        /* Custom range slider styling */
        .range-slider {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
          pointer-events: none;
        }

        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid #a855f7;
          cursor: pointer;
          pointer-events: all;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          position: relative;
          z-index: 10;
        }

        .range-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid #a855f7;
          cursor: pointer;
          pointer-events: all;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          position: relative;
          z-index: 10;
        }

        .range-slider::-webkit-slider-thumb:hover {
          background: #a855f7;
          transform: scale(1.1);
        }

        .range-slider::-moz-range-thumb:hover {
          background: #a855f7;
          transform: scale(1.1);
        }

        .range-slider::-webkit-slider-thumb:active {
          background: #a855f7;
          transform: scale(1.15);
        }

        .range-slider::-moz-range-thumb:active {
          background: #a855f7;
          transform: scale(1.15);
        }

        /* Remove default track styling */
        .range-slider::-webkit-slider-runnable-track {
          background: transparent;
          height: 2px;
        }

        .range-slider::-moz-range-track {
          background: transparent;
          height: 2px;
        }
      `}</style>
    </div>
  );
};

export default TutorsGridPage;